<template>
  <div class="profile-company">

    <div
      v-if="hasCompany"
      class="company-list-wrapper"
    >
      <MyCompany
        v-for="company in companyList"
        :key="company.company_id"
        :company="company"
        @delete="confirmDeleteCompany(company)"
      />
    </div>

    <NoCompanyPlaceholder
      v-else
      @add="addCompany"
    />

    <button
      v-if="hasCompany"
      class="add-button"
      @click="checkSubscriptionBeforeAdd"
      :disabled="isCheckingSubscription"
    >
      <img
        v-if="!isCheckingSubscription"
        src="../../assets/add-icon.png"
        alt="+"
      />

      <div
        v-else
        class="mini-btn-loader"
      ></div>

      Добавить компанию
    </button>


    <!-- SUBSCRIPTION HINT -->

    <div
      v-if="showSubscriptionHint"
      class="subscription-hint"
    >
      <div class="subscription-hint-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6.5 10.2V8.4C6.5 5.4 8.7 3.5 12 3.5C15.3 3.5 17.5 5.4 17.5 8.4V10.2"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />

          <rect
            x="5"
            y="10"
            width="14"
            height="10"
            rx="3"
            stroke="currentColor"
            stroke-width="1.6"
          />

          <circle
            cx="12"
            cy="15"
            r="1.2"
            fill="currentColor"
          />
        </svg>
      </div>

      <div class="subscription-hint-content">

        <div class="subscription-hint-title">
          Доступно в Fullstack HR
        </div>

        <div class="subscription-hint-text">
          Подключите подписку, чтобы добавить несколько компаний
        </div>

      </div>
    </div>


    <div
      v-if="isDeleting"
      class="loader-overlay"
    >
      <div class="loader"></div>
    </div>


    <transition name="fade">

      <div
        v-if="showConfirm"
        class="confirm-overlay"
        :style="{
          pointerEvents:
            backdropReady
              ? 'auto'
              : 'none'
        }"
        @click.self="cancelDelete"
      >
        <div
          class="confirm-dialog"
          @click.stop
        >
          <h3>
            Подтверждение удаления
          </h3>

          <p>
            Вы точно хотите разорвать связь с компанией
            <strong>
              {{ companyToDelete?.name }}
            </strong>?
          </p>

          <div class="buttons">

            <button
              class="btn cancel-btn"
              @click="cancelDelete"
            >
              Отмена
            </button>

            <button
              class="btn delete-btn"
              @click="deleteCompany"
            >
              Разорвать
            </button>

          </div>

        </div>
      </div>

    </transition>

  </div>
</template>


<script setup>

import {
  computed,
  ref,
  nextTick
} from "vue";

import MyCompany
  from "./MyCompany.vue";

import NoCompanyPlaceholder
  from "./NoCompanyPlaceholder.vue";

import UserService
  from "../../api/UserService.js";

import PremiumService
  from "../../api/PremiumService.js";


const props =
  defineProps({
    currentCompany: Object,
    companies: Array
  });


const hasCurrentCompany =
  computed(() => {

    return Boolean(
      props.currentCompany &&
      Object.keys(
        props.currentCompany
      ).length > 0
    );

  });


const companyList =
  computed(() => {

    if (
      Array.isArray(
        props.companies
      ) &&
      props.companies.length > 0
    ) {
      return props.companies;
    }

    if (
      hasCurrentCompany.value
    ) {
      return [
        props.currentCompany
      ];
    }

    return [];

  });


const hasCompany =
  computed(() =>
    companyList.value.length > 0
  );


const hasMultiCompanies =
  computed(() => {

    return (
      companyList.value.length > 1
    );

  });


const companyToDelete =
  ref(null);


const emit =
  defineEmits([
    "open-company-drawer",
    "open-company-drawer-premium",
    "company-removed",
    "open-premium"
  ]);


const isDeleting =
  ref(false);

const showSnackbar =
  ref(false);

const snackbarMessage =
  ref("");

const snackbarType =
  ref("success");

const isCheckingSubscription =
  ref(false);

const showConfirm =
  ref(false);

const backdropReady =
  ref(false);

const showSubscriptionHint =
  ref(false);


let hintTimeout =
  null;


/* ============================================================
   HINT
============================================================ */

function showHint() {

  showSubscriptionHint.value =
    true;

  if (hintTimeout) {
    clearTimeout(
      hintTimeout
    );
  }

  hintTimeout =
    setTimeout(
      () => {

        showSubscriptionHint.value =
          false;

      },
      14000
    );

}


/* ============================================================
   DELETE COMPANY
============================================================ */

async function confirmDeleteCompany(c) {

  companyToDelete.value =
    c;

  showConfirm.value =
    false;

  await nextTick();

  setTimeout(
    () => {

      showConfirm.value =
        true;

      backdropReady.value =
        false;

      setTimeout(
        () => {
          backdropReady.value =
            true;
        },
        100
      );

    },
    0
  );

}


function cancelDelete() {

  if (
    !backdropReady.value
  ) {
    return;
  }

  showConfirm.value =
    false;

}


async function deleteCompany() {

  if (
    !backdropReady.value
  ) {
    return;
  }

  showConfirm.value =
    false;


  if (
    !companyToDelete.value
  ) {

    showError(
      "Компания не выбрана"
    );

    return;

  }


  const removedCompanyId =
    companyToDelete.value
      .company_id;


  isDeleting.value =
    true;


  try {

    if (
      hasMultiCompanies.value
    ) {

      console.log(
        "🗑 Удаляем компанию в MULTI режиме:",
        removedCompanyId
      );

      await UserService
        .removeUserCompanyIfMultiples(
          removedCompanyId
        );

    } else {

      console.log(
        "🗑 Удаляем компанию в SINGLE режиме"
      );

      await UserService
        .removeUserCompany();

    }


    emit(
      "company-removed",
      removedCompanyId
    );

  } catch (error) {

    console.error(
      "Ошибка при удалении компании:",
      error
    );

    showError(
      "Ошибка при удалении компании"
    );

  } finally {

    isDeleting.value =
      false;

    companyToDelete.value =
      null;

  }

}


function showError(message) {

  snackbarMessage.value =
    message;

  snackbarType.value =
    "error";

  showSnackbar.value =
    true;

}


/* ============================================================
   ADD COMPANY
============================================================ */

function addCompany() {

  emit(
    "open-company-drawer"
  );

}


async function checkSubscriptionBeforeAdd() {

  if (
    isCheckingSubscription.value
  ) {
    return;
  }


  isCheckingSubscription.value =
    true;

  showSubscriptionHint.value =
    false;


  try {

    const premium =
      await PremiumService
        .fetchMySubscription();


    if (!premium) {

      throw new Error(
        "Сервер не вернул настройки подписки"
      );

    }


    console.log(
      "📦 Проверка добавления компании:",
      premium.toJSON()
    );


    if (
      premium.shouldShowCompanyPaywall
    ) {

      console.log(
        "💳 Показываем paywall"
      );


      showHint(
        "Для добавления ещё одной компании необходим тариф Fullstack HR"
      );


      emit(
        "open-premium"
      );

      return;

    }


    console.log(
      "✅ Добавление компании разрешено"
    );


    emit(
      "open-company-drawer-premium"
    );

  } catch (error) {

    console.error(
      "❌ Ошибка проверки подписки:",
      error.response?.data ||
      error.message ||
      error
    );


    showHint(
      "Не удалось проверить подписку. Попробуйте ещё раз",
      "error"
    );

  } finally {

    isCheckingSubscription.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.profile-company {
  padding: 10px 0;
}


/* ============================================================
   ADD BUTTON
============================================================ */

.add-button {
  display: flex;
  align-items: center;

  gap: 10px;

  margin-top: 30px;
  margin-left: 10px;

  padding: 0;

  color: #111;

  background: none;

  border: none;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 16px;

  cursor: pointer;
}


html[data-theme="dark"]
.add-button {
  color: #eee;
}


.add-button img {
  width: 24px;
  height: 24px;
}


.add-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}


/* ============================================================
   MINI BUTTON LOADER
============================================================ */

.mini-btn-loader {
  width: 20px;
  height: 20px;

  box-sizing: border-box;

  border:
    3px solid
    rgba(0, 0, 0, 0.2);

  border-top-color:
    #2173e5;

  border-radius:
    50%;

  animation:
    spin
    .6s
    linear
    infinite;
}


/* ============================================================
   SUBSCRIPTION HINT
============================================================ */

.subscription-hint {
  position: relative;

  width: fit-content;
  max-width: 300px;

  margin-top: 14px;
  margin-left: 10px;

  display: flex;
  align-items: center;

  gap: 10px;

  box-sizing: border-box;

  padding:
    11px
    13px;

  color:
    #424a55;

  background:
    #f6f8fc;

  border:
    1px solid
    rgba(55, 127, 228, 0.12);

  border-radius:
    14px;

  box-shadow:
    0
    8px
    20px
    rgba(15, 23, 42, 0.045);

  animation:
    subscriptionHintAppear
    .25s
    cubic-bezier(
      .22,
      1,
      .36,
      1
    );
}


/* ============================================================
   REAL TRIANGLE TAIL
============================================================ */

/*
  Внешний треугольник.
  Он играет роль border хвостика.
*/

.subscription-hint::before {
  content: "";

  position: absolute;

  top: -9px;
  left: 21px;

  width: 0;
  height: 0;

  border-left:
    9px solid transparent;

  border-right:
    9px solid transparent;

  border-bottom:
    9px solid
    rgba(55, 127, 228, 0.12);
}


/*
  Внутренний треугольник.
  Закрывает центр внешнего и
  становится продолжением карточки.
*/

.subscription-hint::after {
  content: "";

  position: absolute;

  top: -7px;
  left: 22px;

  width: 0;
  height: 0;

  border-left:
    8px solid transparent;

  border-right:
    8px solid transparent;

  border-bottom:
    8px solid #f6f8fc;
}


/* ============================================================
   HINT ICON
============================================================ */

.subscription-hint-icon {
  width: 32px;
  height: 32px;

  flex:
    0
    0
    32px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #5d89c5;

  background:
    rgba(55, 127, 228, 0.08);

  border:
    1px solid
    rgba(55, 127, 228, 0.08);

  border-radius:
    10px;
}


.subscription-hint-icon svg {
  width: 16px;
  height: 16px;
}


/* ============================================================
   HINT CONTENT
============================================================ */

.subscription-hint-content {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 3px;
}


.subscription-hint-title {
  color:
    #3f5877;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  line-height: 1.25;
}


.subscription-hint-text {
  color:
    #858c95;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  line-height: 1.4;
}


/* ============================================================
   HINT DARK
============================================================ */

html[data-theme="dark"]
.subscription-hint {
  color:
    #e5e8ec;

  background:
    #22252b;

  border-color:
    rgba(110, 155, 215, 0.12);

  box-shadow:
    0
    10px
    24px
    rgba(0, 0, 0, 0.16);
}


html[data-theme="dark"]
.subscription-hint::before {
  border-bottom-color:
    rgba(110, 155, 215, 0.12);
}


html[data-theme="dark"]
.subscription-hint::after {
  border-bottom-color:
    #22252b;
}


html[data-theme="dark"]
.subscription-hint-icon {
  color:
    #91b6ee;

  background:
    rgba(91, 158, 255, 0.075);

  border-color:
    rgba(91, 158, 255, 0.08);
}


html[data-theme="dark"]
.subscription-hint-title {
  color:
    #b9cdea;
}


html[data-theme="dark"]
.subscription-hint-text {
  color:
    #838b95;
}


/* ============================================================
   HINT ANIMATION
============================================================ */

@keyframes subscriptionHintAppear {

  from {
    opacity: 0;

    transform:
      translateY(5px)
      scale(.98);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }

}


/* ============================================================
   CONFIRM OVERLAY
============================================================ */

.confirm-overlay {
  position: fixed;

  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(0, 0, 0, 0.5);

  z-index: 2000;

  opacity: 1;
}


/* ============================================================
   CONFIRM DIALOG
============================================================ */

.confirm-dialog {
  width: 90%;
  max-width: 400px;

  padding:
    30px
    40px;

  box-sizing: border-box;

  background: white;

  border-radius: 16px;

  box-shadow:
    0
    12px
    40px
    rgba(0, 0, 0, 0.15);

  text-align: center;

  font-size: 20px;
}


.confirm-dialog h3 {
  margin-bottom: 20px;

  font-weight: 700;

  font-size: 20px;
}


.confirm-dialog p {
  margin-bottom: 30px;

  color: #444;

  font-size: 14px;
}


.confirm-dialog strong {
  color: #2173e5;
}


/* ============================================================
   CONFIRM BUTTONS
============================================================ */

.buttons {
  display: flex;
  justify-content: space-between;

  gap: 20px;
}


.btn {
  flex: 1;

  padding:
    12px
    0;

  border: none;
  border-radius: 12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;
  font-weight: 600;

  cursor: pointer;

  transition:
    background-color
    .3s
    ease;
}


.cancel-btn {
  background: #ddd;
  color: #333;
}


.cancel-btn:hover {
  background: #ccc;
}


.delete-btn {
  background: #e65122;
  color: white;
}


.delete-btn:hover {
  background: #bf3f15;
}


/* ============================================================
   CONFIRM DARK
============================================================ */

html[data-theme="dark"]
.confirm-dialog {
  color: #eee;

  background: #2a2a2a;

  box-shadow:
    0
    12px
    40px
    rgba(0, 0, 0, 0.7);
}


html[data-theme="dark"]
.confirm-dialog p {
  color: #ccc;
}


html[data-theme="dark"]
.cancel-btn {
  color: #ddd;

  background: #555;
}


html[data-theme="dark"]
.cancel-btn:hover {
  background: #444;
}


html[data-theme="dark"]
.delete-btn {
  background: #bf3f15;
}


html[data-theme="dark"]
.delete-btn:hover {
  background: #a02e11;
}


/* ============================================================
   LOADER
============================================================ */

.loader-overlay {
  position: fixed;

  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(0, 0, 0, 0.3);

  z-index: 3000;
}


.loader {
  width: 48px;
  height: 48px;

  box-sizing: border-box;

  border:
    6px solid
    #f3f3f3;

  border-top:
    6px solid
    #2173e5;

  border-radius:
    50%;

  animation:
    spin
    1s
    linear
    infinite;
}


/* ============================================================
   COMPANY LIST
============================================================ */

.company-wrapper {
  display: flex;

  justify-content:
    flex-start;
}


.multi-company-wrapper {
  display: flex;
  flex-direction: column;

  gap: 18px;

  margin-left: 10px;
}


.company-list-wrapper {
  display: flex;
  flex-direction: column;

  gap: 18px;

  margin-left: 10px;
}


/* ============================================================
   TRANSITIONS
============================================================ */

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity
    .2s
    ease;
}


.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}


.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}


/* ============================================================
   SPINNER
============================================================ */

@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}

</style>